import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { DStructField, DType, dTypeCreatePG, dTypeGetDescription, dTypeGetGoName, dTypeGetPGName, d_type_kind_values, d_type_modifier_values, d_types } from '../lib/mdl/d';
import { UIInputAdapter } from '../lib/ui/component/adapter';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UISelect } from '../lib/ui/component/select';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { UIScreen } from '../lib/ui/screen';
import { UIValue } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $log } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $form } from '../lib/y/form';
import { $location } from '../lib/y/location';
import { YId } from '../lib/y/types';

class TypeScreen extends UIScreen {
    constructor(private _type_map: Record<YId, DType>, private _type?: DType) {
        super();
        this.title = _type?.name || 'New type';
    }

    protected override fillContent(content: UIViewSpec): void {
        const type_values: UIValue<YId>[] = Object.entries(this._type_map).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(e => ({ key: e[0], name: e[1].name }));
        const type = this._type;

        const name = new UITextInput({ required: true });
        const kind = new UISelect(d_type_kind_values, { required: true });

        const builtin_pg_type = new UITextInput({ undefined_if_empty: true });
        const builtin_pg_type_mod = new UITextInput({ undefined_if_empty: true });
        const builtin_ch_type = new UITextInput({ undefined_if_empty: true });
        const go_type = new UITextInput({ undefined_if_empty: true });
        const go_package = new UITextInput({ undefined_if_empty: true });
        const ts_type = new UITextInput({ undefined_if_empty: true });

        const struct_fields = new UIInputAdapter((_s: DStructField, entry) => {
            const container = $el.DIV('l-input-row');
            const name = new UITextInput({ required: true });
            const type_id = new UISelect(type_values, { required: true });
            const modifier = new UISelect(d_type_modifier_values, {});
            const is_nullable = new UICheckbox({ title: 'Nullable' });
            const remove = new UIButton({ icon: UIIcon.TRASH_CAN, on_click: () => { struct_fields.remove(entry) } });
            container.ui_add([
                new UILabeled(name, { above: 'Name', required: true }),
                new UILabeled(type_id, { above: 'Type', required: true }),
                new UILabeled(modifier, { above: 'Modifier' }),
                is_nullable,
                remove
            ]);

            return {
                view: container,
                input: $form({ id: $form.noop(), name, type_id, modifier, is_nullable })
            };
        });

        const struct_pg_json = new UICheckbox({ title: 'Store as JSON in PG database' });
        const struct_pg_split = new UICheckbox({ title: 'Split struct across multiple columns in PG database' });
        const struct_ch_split = new UICheckbox({ title: 'Split struct across multiple columns in CH database' });
        const struct_fields_list = new UIList(struct_fields, { classes: 'l-form' });
        const struct_fields_add = new UIButton({ title: '+ Add field', kind: 'k-text', on_click: () => { struct_fields.push({} as DStructField, true) } });

        const map_key_type_id = new UISelect(type_values);
        const map_value_type_id = new UISelect(type_values);

        const enum_values = new UIInputAdapter((_s: string, entry) => {
            const container = $el.DIV('l-input-row');
            const value = new UITextInput({ required: true });
            const remove = new UIButton({ icon: UIIcon.TRASH_CAN, on_click: () => { enum_values.remove(entry) } });
            container.ui_add([
                value,
                remove
            ]);

            return {
                view: container,
                input: value
            };
        });

        const enum_pg_string = new UICheckbox({ title: 'Store as string in PG database' });
        const enum_go_is_valid = new UICheckbox({ title: 'Add IsValid method in Go' });
        const enum_values_list = new UIList(enum_values, { classes: 'l-form' });
        const enum_values_add = new UIButton({ title: '+ Add value', kind: 'k-text', on_click: () => { enum_values.push('', true) } });

        const form = $form({ name, kind, builtin_pg_type, builtin_pg_type_mod, builtin_ch_type, go_type, go_package, ts_type, struct_pg_json, struct_pg_split, struct_ch_split, struct_fields, map_key_type_id, map_value_type_id, enum_pg_string, enum_go_is_valid, enum_values });

        const builtin_pg_row = $el.DIV('l-input-row', [
            new UILabeled(builtin_pg_type, { above: 'PostgreSQL type name' }),
            new UILabeled(builtin_pg_type_mod, { above: 'Modifier (collation)' }),
        ]);

        const builtin_go_row = $el.DIV('l-input-row', [
            new UILabeled(go_type, { above: 'Go type' }),
            new UILabeled(go_package, { above: 'Go package' }),
        ]);

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(name, { above: 'Name' }),
            new UILabeled(kind, { above: 'Kind' }),
            builtin_pg_row,
            new UILabeled(builtin_ch_type, { above: 'ClickHouse type name' }),
            builtin_go_row,
            new UILabeled(ts_type, { above: 'TypeScript type name' }),
            struct_pg_json,
            struct_pg_split,
            struct_ch_split,
            new UILabeled(struct_fields_list, { above: 'Fields' }),
            struct_fields_add,
            new UILabeled(map_key_type_id, { above: 'Key type' }),
            new UILabeled(map_value_type_id, { above: 'Value type' }),
            enum_pg_string,
            enum_go_is_valid,
            new UILabeled(enum_values_list, { above: 'Values' }),
            enum_values_add
        ]);

        $on(kind, 'value', v => {
            builtin_pg_row.ui_show(undefined, v == 'builtin');
            builtin_ch_type.ui_show(undefined, v == 'builtin');
            builtin_go_row.ui_show(undefined, v == 'builtin' || v == 'enum' || v == 'struct');
            ts_type.ui_show(undefined, v == 'builtin');
            struct_pg_json.ui_show(undefined, v == 'struct');
            struct_pg_split.ui_show(undefined, v == 'struct');
            struct_ch_split.ui_show(undefined, v == 'struct');
            struct_fields_list.ui_show(undefined, v == 'struct');
            struct_fields_add.ui_show(undefined, v == 'struct');
            map_key_type_id.ui_show(undefined, v == 'map');
            map_value_type_id.ui_show(undefined, v == 'map');
            enum_pg_string.ui_show(undefined, v == 'enum');
            enum_go_is_valid.ui_show(undefined, v == 'enum');
            enum_values_list.ui_show(undefined, v == 'enum');
            enum_values_add.ui_show(undefined, v == 'enum');
        })

        if(type) {
            form.value = type;
        } else {
            kind.value = 'builtin';
        }

        content.ui_add([
            section,
            makeActionButton(UIIcon.THUMBS_UP, type ? 'Update' : 'Create', () => {
                if(!form.validate()) return;

                $log('app', 'value', form.value);

                d_types.upsert(type?.id, form.value).then(() => {
                    $location.pop();
                }, uiToast);
            })
        ]);
    }
}

export class AdminTypesScreen extends UIScreen {
    private _source!: YArrayDataSource<DType>;
    private _all_types!: DType[];
    private _all_type_map!: Record<YId, DType>;

    constructor() {
        super();
    }

    protected override async fillContent(content: UIViewSpec) {
        this._all_types = await d_types.getAll();
        this._all_type_map = Object.fromEntries(this._all_types.map(t => [t.id, t]));

        const doSearch = () => this._doSearch();

        const filter_bar = new ABFilterBar({ on_search: doSearch, on_filter: doSearch });

        // filters.addCustom('event', $i18n.pr.filter_events, all_events.map(e => ({ key: e.model.id, name: e.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_events_all);
        // filters.addCustom('venue', $i18n.pr.filter_venue, all_venues.map(v => ({ key: v.model.id, name: v.model.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_venues_all);
        // // filters.addCustom('city', $i18n.pr.filter_city, all_cities.map(c => ({ key: c.id, name: c.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_cities_all);
        // filters.addDateRange('date', $i18n.pr.filter_time_period);

        const def = new UITableDefinition<DType>();
        def.row_classes = 'tl-admin-table-row'; // check if we really need the tl-admin-table-row

        def.addCustom({
            width: -24, classes: 'tl-thumb-cell', decorator: (td, row) => {
                const kind = row.item.kind;
                td.append($el.DIV(['o-data-type-icon', 'm-' + kind], kind.slice(0, 1)));
            }, comparator: (lhs, rhs, order) => lhs.kind.localeCompare(rhs.kind) * order
        });

        def.addValue(t => t.name, { title: 'Name', width: 125 });
        def.addValue(t => dTypeGetPGName(this._all_type_map, t), { title: 'PG type', width: 125 });
        def.addValue(t => dTypeGetGoName(this._all_type_map, t), { title: 'Go type', width: 125 });
        def.addValue(t => dTypeGetDescription(this._all_type_map, t), { title: '', width: 125 });

        def.addButtons([
            {
                icon: UIIcon.DATABASE, tooltip: 'Create PG type', action: item => {
                    return dTypeCreatePG(item.id).catch(uiToast);
                }
            }, {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push({ transient: new TypeScreen(this._all_type_map, item) });
                }
            }
        ], { width: -62 });

        def.preference_prefix = 'd_types';

        content.ui_add([
            $el.SECTION('', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, this._source = new YArrayDataSource),
            ])),
            makeActionButton(UIIcon.PLUS, 'Create new', () => {
                $location.push({ transient: new TypeScreen(this._all_type_map) });
            })
        ]);

        doSearch();
    }

    private _doSearch() {
        // const matcher = this.filter_matcher;
        const types = this._all_types
        this._source.data = types;
    }

    override onViewDidReappear() {
        d_types.getAll().then(types => {
            this._all_types = types;
            this._all_type_map = Object.fromEntries(types.map(t => [t.id, t]));
            this._doSearch();
        });
    }
}
